import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, Injector, inject, signal } from '@angular/core'
import { FieldTree, form } from '@angular/forms/signals'
import { MatPaginator } from '@angular/material/paginator'
import { MatSort } from '@angular/material/sort'
import { ActivatedRoute, Router } from '@angular/router'
import { Observable, Subject, firstValueFrom } from 'rxjs'
import { debounceTime, takeUntil } from 'rxjs/operators'

import { LaskunAsiakas } from '@lemon/core-firebase/model/lasku'
import { LaskuUriService } from '@lemon/core-firebase/service/lasku/lasku-uri.service'

import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { DebugService } from 'app/_angular/service/debug.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { arvonMuutokset } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { Asiakas } from 'app/_jaettu-lemonator/model/asiakas'

import { map } from 'rxjs/operators'
import { LaskuAsiakkaatSelaaDataSourceService } from './lasku-asiakkaat-selaa.datasource.service'

interface HakuLomake {
  haku: string
}

@Component({
  templateUrl: './lasku-asiakkaat-selaa.component.html',
  styleUrls: ['./lasku-asiakkaat-selaa.component.css'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [LaskuAsiakkaatSelaaDataSourceService],
})
export class LaskuAsiakkaatSelaaComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator
  @ViewChild(MatSort, { static: true }) sort: MatSort

  private _ngUnsubscribe = new Subject<void>()
  private readonly _injector = inject(Injector)

  readonly asiakas = signal<Asiakas | null>(null)

  lomake: FieldTree<HakuLomake>

  rivienMaaraObservable: Observable<number>
  lataaObservable: Observable<boolean>
  asiakkaatObservable: Observable<LaskunAsiakas[]>
  kirjanpitajaOnDevaajaObservable: Observable<boolean>
  laskuAsiakkaatUriObservable: Observable<string>
  laskuAsiakkaatEncodedUriObservable: Observable<string>

  displayedColumns = ['ulkoinenTunniste', 'nimi', 'ytunnus', 'asiakastyyppi', 'postitmp', 'sahkoinen']

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _asiakasService: AsiakasService,
    private _laskuUriService: LaskuUriService,
    private _debugService: DebugService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    public dataSource: LaskuAsiakkaatSelaaDataSourceService,
  ) {
    this.rivienMaaraObservable = this.dataSource.rivienMaaraObservable
    this.lataaObservable = this.dataSource.lataaObservable
    this.asiakkaatObservable = this.dataSource.nakyvatAsiakkaatObservable
  }

  ngOnInit() {
    this.kirjanpitajaOnDevaajaObservable = this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaObservable
    this.laskuAsiakkaatUriObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      map(asiakas => (asiakas?.avain ? this._laskuUriService.getLaskuAsiakkaatCollectionUri(asiakas.avain) : '')),
    )
    this.laskuAsiakkaatEncodedUriObservable = this.laskuAsiakkaatUriObservable.pipe(map(uri => this._debugService.createFirestoreLinkLemonaid(uri)))

    this._asiakasService.nykyinenAsiakasObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(asiakas => {
      this.asiakas.set(asiakas)
    })

    this.lomake = form(signal<HakuLomake>({ haku: '' }), { injector: this._injector })

    arvonMuutokset(this.lomake.haku, { injector: this._injector })
      .pipe(debounceTime(300), takeUntil(this._ngUnsubscribe))
      .subscribe(arvo => {
        this.dataSource.setSearch(arvo ?? '')
      })
  }

  ngAfterViewInit() {
    const initial = this.dataSource.sort
    this.sort.sort({ id: initial.active, start: initial.direction === 'asc' ? 'asc' : 'desc', disableClear: true })
    this.sort.sortChange.pipe(takeUntil(this._ngUnsubscribe)).subscribe(sort => this.dataSource.setSort(sort))
    this.paginator.page.pipe(takeUntil(this._ngUnsubscribe)).subscribe(page => this.dataSource.setPage(page))
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  async avaa(rivi: LaskunAsiakas) {
    const avainTiedot = await firstValueFrom(this._asiakasService.nykyinenAsiakasAvainObservable)
    if (!avainTiedot?.avain) {
      return
    }
    this._router.navigate(['asiakkaat', avainTiedot.avain, 'lasku-asiakkaat', 'selaa', rivi.avain])
  }
}
